/**
 * The start screen's composition root. `renderGameStart` assembles the screen from named section
 * builders (pending banner, logo, name slot, reference strip, mode cards, Multiplayer section,
 * control bank, Play), mounts it on `#skat-app` alongside the screen's own menu button, hands the
 * assembly to the layout engine, then starts the entrance — and returns that entrance's
 * build-completion promise to its callers. The name slot is built and owned here (`buildNameSlot`
 * plus the exported `refreshNameSlot` over a module-level render handle), as is the identity
 * trigger that doors into the profile overlay. So is Play's commit path: `commitAndPlay` fires the
 * screen exit, races the lazy game graph alongside it, and mounts the cold-boot indicator only
 * when the graph loses that race. `resetFreshnessGates` clears the reveal-freshness dataset keys
 * at the game-start boundary, and the three menu crossings re-exported below keep this file the
 * package's one public interface (Boundary, this directory's CLAUDE.md).
 */
import { t } from "../i18n.js";
import { getApp } from "../state.js";
import { readLastSelected, writeLastSelected } from "../player-profile.js";
import {
  hasActiveRun,
  activeRunsReady,
  resetActiveRuns,
  loadActiveRuns,
  updateModeCardBadge,
  refreshActiveRunVisuals,
} from "./game-start-active-runs.js";
import {
  createChalice,
  createFallenChalice,
} from "../icons/game-type-icons.js";
import { createGateEnter } from "../icons/prompt-icons.js";
import { armButtonPlate } from "../animation/button-plate.js";
import { createSuitPipSvg } from "../cards/cards.js";
import { suitColor, scheduleIdle } from "../utils.js";
import { buildControlBank } from "./game-start-control-bank.js";
import { buildMultiplayerSection } from "./game-start-multiplayer-section.js";
import { buildReferenceStrip } from "./game-start-reference-strip.js";
import { startSession } from "./game-start-session.js";
import { enterFullscreen } from "../fullscreen.js";
import { getRegisteredName } from "./game-start-auth.js";
import {
  openProfileOverlay,
  registerStartSettings,
} from "./game-start-profile-open.js";
import {
  consumeIdentityFlash,
  playIdentityConfirmation,
} from "./game-start-identity-confirmation.js";
import { createPersonaKnight } from "../icons/persona-icons.js";
import { IDENTITY_COLOR } from "../constants.js";
import { buildWordmarkSvg } from "./game-start-wordmark.js";
import { buildPlayLabelSvg } from "./game-start-play-label.js";
import { buildModeLabelSvg } from "./game-start-mode-label.js";
import { buildBoardPendingBanner } from "./game-start-pending-banner.js";
import { maybeShowPersonalGreeting } from "../personal-greetings.js";
import {
  playGameStartEntrance,
  playGameStartExit,
} from "./game-start-entrance.js";
import {
  placeStartAssembly,
  ensureStartReflowWired,
} from "./game-start-layout.js";
import { warmCardTemplates } from "../cards/card-template-cache.js";
import { buildMenuButton } from "../menu-button.js";
import { ensureGameUi, warmGameGraph } from "../game-graph.js";
import { armGlare } from "../animation/glare.js";

// Re-exported, not just imported, so game-menu.js's Menu-entry doorway into the profile overlay
// (identity-overlay-menu-entry task; the profile overlay since profile-overlay-and-menu-slim) can
// go through this file — the package's sole public interface (see the Boundary note in this
// directory's CLAUDE.md) — rather than reaching directly into game-start-profile-open.js.
// The old `getRegisteredName`/`refreshPickersGroup` crossings retired with the same rework: the
// Menu's Profil entry wears a stable label in both registration states, and the settings that
// could go stale live on the overlay now, which nudges the bank from inside this package.
export { openProfileOverlay } from "./game-start-profile-open.js";
// The second crossing (multiplayer-entry-and-notifications design § "The in-game Menu entry") —
// game-menu.js's Multiplayer entry calls this one-shot flag-setter to force the section open on
// the start screen it's about to show.
export { requestMultiplayerSectionOpen } from "./game-start-multiplayer-section.js";
// The third crossing (nav-icon-candidate-placements task) — `refreshNameSlot` (below, defined
// directly in this file since `buildNameSlot` already lives here) is the real name-slot refresh
// game-menu.js's Profil entry passes into `openProfileOverlay` when it opens from the start-screen
// menu, replacing the in-game entry's own no-op (game-menu.js#identityEntry).

// why: both sizes are the SVG-attr/viewBox base; skat.css mirrors them as the base px
// of its elastic overrides (`.game-start-logo-pip-group svg`, `.mode-icon svg`) — change
// one side, change the other.
const LOGO_PIP_SIZE = 54;
const MODE_ICON_SIZE = 46;
const MODE_ICON_COLOR = "#94A3B8";
// why: the SVG-attr/viewBox base for the registered row's Knight bust; skat.css mirrors it as the
// base px of its elastic override (`.game-start-name-persona svg`) — change one side, change the other.
const NAME_PERSONA_SIZE = 28;

const GAMEMODE_LABEL_KEY = {
  SKAT: "modeSkat",
  RAMSCH: "modeRamsch",
};

/** Clear the dataset keys that gate reveal-animation freshness across renders.
 *  Called at the game-start boundary because the keys include round number but
 *  no session identity — round 1 of a new session can collide with round 1
 *  of the prior session and silently suppress the bid pulse / hint reveal /
 *  result fade-in / declaration entrance. Moved from game.js (lazy-game-graph-boot-seam):
 *  its two consumers are boot.js's pristine-boot renderStartScreen and game.js's own
 *  start branch, which already imports renderGameStart from this module. */
export function resetFreshnessGates(appEl) {
  delete appEl.dataset.lastBidKey;
  delete appEl.dataset.lastHintKey;
  delete appEl.dataset.lastReasonKey;
  delete appEl.dataset.lastResultRound;
  delete appEl.dataset.lastDeclaredGameType;
}

// The Play button always reads the fixed, localized "Spielen" / "Play" word —
// never the dynamic run context, so it can be etched from pre-extracted glyphs.
// The run context (the round) lives on the mode card that owns the run.
function playLabelWord() {
  return t("gameStartPlayStart");
}

/**
 * Prepare a heritage icon SVG for CSS-driven recoloring via `currentColor`.
 * Sets stroke to currentColor on the SVG root (cascades to children via SVG inheritance),
 * and swaps hardcoded fills (cloth, finials) to currentColor so CSS `color` controls everything.
 * Also clears inline fill/stroke styles set by identity-aware factories so the start screen
 * can drive color through `color:` on the wrapper.
 */
function makeRecolorable(svgEl) {
  svgEl.setAttribute("stroke", "currentColor");
  for (const child of svgEl.querySelectorAll("*")) {
    // Identity-aware factories fill the cloth via inline style (so no host CSS
    // can recolor identity). On the start screen we want the cloth to follow currentColor
    // instead — clear the inline style and route through the fill attribute.
    const hadInlineFill = !!child.style.fill;
    if (hadInlineFill) child.style.fill = "";
    if (child.style.stroke) child.style.stroke = "";
    const fill = child.getAttribute("fill");
    const needsCurrentColor =
      hadInlineFill || (fill && fill !== "none" && fill !== "currentColor");
    if (needsCurrentColor) {
      child.setAttribute("fill", "currentColor");
    }
  }
}

// `pristine` gates game-start-multiplayer-section.js's turn-notification stamp-vs-refetch
// choice, threaded through to game-start-turn-banners.js's own two data sources (that module's
// file header carries the why): `true` only from boot.js#renderStartScreen, the genuine first
// paint; `game.js#renderGameStartScreen` (every Quit/error-recovery return) always passes `false`.
export function renderGameStart(sessionLost = false, pristine = false) {
  const container = document.createElement("div");
  container.className = "game-start";
  if (sessionLost) appendSessionLostNotice(container);

  resetActiveRuns(); // patched in by loadActiveRuns once GET /api/runs resolves, below
  const settings = buildInitialSettings();
  // The one live settings object for this screen — every profile-overlay opener writes through
  // this same instance rather than a snapshot of it (profile-overlay-and-menu-slim § Settings
  // ownership), so a rename or a toggle flip made behind the Menu reaches what Play sends.
  registerStartSettings(settings);
  container.appendChild(buildGameStartContent(settings, pristine));
  const appEl = getApp();
  appEl.appendChild(container);
  // The `page-scroll`/`game-start-open` html-class mirror (phone-style-recalc-stall-
  // class — was `html:has(.game-start)` / `body:has(.game-start)`): the invariant is
  // "present exactly while a .game-start element is connected", so this mount is the
  // one place they turn on. game.js#clearAppForRender is the sole removal seam
  // (every full render sweeps #skat-app unconditionally before either branch runs),
  // so a game-start→game-start re-render (e.g. Quit) sees them dropped then
  // immediately re-added here, never a demotion gap. skat.css § Layout carries the
  // full why (the document-anchor invalidation mechanism this replaced).
  document.documentElement.classList.add("page-scroll", "game-start-open");
  // The start screen's own menu-trigger instance — `menu-button-start`, not
  // `menu-button`, so it does NOT trip the `html.in-game-chrome` context flip
  // (menu-button.js#renderMenuButton) that hides the footer. A sibling of
  // `.game-start` on #skat-app, positioned the same as the in-game button (skat.css,
  // next to `.menu-button`'s rule).
  appEl.appendChild(buildMenuButton("menu-button-start"));
  // Place + size the assembly (compaction on cramped viewports, fill-up on large)
  // before the entrance measures/animates. The engine owns placement; CSS applies.
  placeStartAssembly(container);
  // The menu button now has real --menu-* sizing (placeStartAssembly's own
  // applyMenuAffordanceVars pass, just above) — arm its sun-anchored glare
  // (docs/dom/AFFORDANCES). Idempotent across rounds: #skat-app is persistent, so
  // armGlare wires the pointer listener once, ever, no matter which screen gets here
  // first.
  armGlare(appEl);
  ensureStartReflowWired();
  maybeShowPersonalGreeting();
  // A failed entrance must not break the start screen (animation-error
  // convention: warn and continue). Wrapped at the call site so the function
  // still returns its timeline for the unit test.
  try {
    playGameStartEntrance(container);
  } catch (e) {
    console.warn("[game-start-entrance]", e.message);
  }
  playStashedIdentityConfirmation(container);
  // Both idle warmups wait for the entrance to finish playing (tbt-reshaping slice
  // 1 §3) — under throttle they used to land mid-choreography as their own long
  // tasks; deferring them past _entranceDone moves that cost off the entrance's
  // critical path entirely. The bail path resolves _entranceDone immediately, so
  // a return-to-start / reduced-motion visit schedules them right away, same as
  // before. warmCardTemplates pre-pays the deal-burst card construction (32 faces
  // + 3 backs); warmGameGraph fetch-preloads the lazy graph's bytes so a later
  // Play rarely hits a cold import() — neither gates the loader or the deal path,
  // and both are idempotent.
  void Promise.resolve(container._entranceDone).then(() => {
    scheduleIdle(warmCardTemplates);
    scheduleIdle(warmGameGraph);
  });
  void loadActiveRuns(settings);
  return container._entranceBuilt ?? Promise.resolve();
}

/**
 * Consume a post-login/logout reload's stashed flash (identity-action-confirmation design) and
 * play the beat once the entrance has released the slot — a beat fired straight off this
 * synchronous render would etch inside a still-invisible `.game-start-name-slot` (the entrance
 * animates it in from `autoAlpha: 0`). `container._entranceDone` resolves immediately on the
 * reduced-motion / intro-already-played bail, so a return visit's beat (were one ever to arrive
 * here without a reload) plays without a real wait. Consuming synchronously — before the await —
 * is what makes this remove-on-read: a re-render before the beat plays can't see the token twice.
 * Wrapped in a fire-and-forget IIFE so `renderGameStart` stays synchronous for its callers/tests.
 */
function playStashedIdentityConfirmation(container) {
  const kind = consumeIdentityFlash();
  if (kind == null) return;
  void (async () => {
    await Promise.resolve(container._entranceDone);
    playIdentityConfirmation(kind, getRegisteredName());
  })();
}

function appendSessionLostNotice(container) {
  const notice = document.createElement("div");
  notice.className = "session-lost-notice";
  notice.textContent = t("sessionLost");
  container.appendChild(notice);
}

function buildInitialSettings() {
  const last = readLastSelected();
  return {
    gameMode: GAMEMODE_LABEL_KEY[last?.gameMode] ? last.gameMode : "SKAT",
    // Default on; remember an explicit off. (Opposite test to learningMode,
    // which defaults off — hence `!== "false"` vs `=== "true"`.)
    hints: localStorage.getItem("hints") !== "false",
    learningMode: localStorage.getItem("instantHints") === "true",
    playerName: localStorage.getItem("playerName") || "",
  };
}

function buildGameStartContent(settings, pristine) {
  const content = document.createElement("div");
  content.className = "game-start-content";
  const pendingBanner = buildBoardPendingBanner();
  if (pendingBanner != null) content.appendChild(pendingBanner);
  content.appendChild(buildLogo());
  const nameSlot = buildNameSlot(settings);
  content.appendChild(nameSlot.slot);
  // The unfolded-menu strip (start-screen-unfolded-menu-strip design): the app's look-into
  // surfaces, one row below the name slot — the first thing a newcomer with learning intent
  // sees, ahead of the mode choice.
  content.appendChild(buildReferenceStrip());
  content.appendChild(buildModeCards(settings));
  // The your-turn notifications live INSIDE this section now (start-screen-layout-improvements
  // part B, `.mp-panel-notice`) — nothing mounts above the logo for them any more; `pristine`
  // threads through to pick the section's stamp-vs-refetch data source.
  content.appendChild(buildMultiplayerSection(nameSlot.renderState, pristine));
  content.appendChild(buildControlBank(settings));
  content.appendChild(buildPlayButton(settings));
  // Reveal-when-earned: the leaderboard is a reward for playing, not a first-visit greeting. Since
  // slice 2b `activeRuns` is server-sourced and starts empty at this synchronous point (the `GET
  // /api/runs` fetch hasn't resolved yet), so the section can't be decided here — it's appended
  // reactively by refreshActiveRunVisuals/loadActiveRuns once the fetch answers "has this player
  // played anything?". It still rides at the bottom of the vertical composition (append-order);
  // the engine scales the whole stack to fill 1/φ.
  return content;
}

function buildLogo() {
  const logo = document.createElement("div");
  logo.className = "game-start-logo";
  logo.appendChild(
    buildPipGroup([
      createFallenChalice(LOGO_PIP_SIZE),
      pipSvg("diamonds"),
      pipSvg("hearts"),
    ]),
  );
  // The wordmark is glyph-path SVG (not DOM text) so the entrance can etch it
  // letter-by-letter alongside the pips. The etch sets stroke attrs / tweens
  // `fill`, which don't resolve CSS vars — resolve --text-primary to a concrete
  // color here (its solid factory state, what reduced-motion / no-GSAP shows).
  logo.appendChild(buildWordmarkSvg(resolveInkColor()));
  logo.appendChild(
    buildPipGroup([
      pipSvg("spades"),
      pipSvg("clubs"),
      createChalice(LOGO_PIP_SIZE),
    ]),
  );
  return logo;
}

function pipSvg(suit) {
  return createSuitPipSvg(suit, LOGO_PIP_SIZE, suitColor(suit));
}

/** --text-primary as a concrete color for the wordmark glyphs (falls back to vellum). */
function resolveInkColor() {
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue("--text-primary")
    .trim();
  return v || "#F1F5F9";
}

function buildPipGroup(children) {
  const group = document.createElement("div");
  group.className = "game-start-logo-pip-group";
  for (const child of children) group.appendChild(child);
  return group;
}

// The name slot's own render function, held module-level so `refreshNameSlot` (below) can reach
// the live instance from outside this closure — a callback-injection variable (js/CLAUDE.md §
// Module-level state exceptions), the same shape `game-start-control-bank.js#refreshPickersGroup`
// would use if its target weren't reachable by a stable DOM selector: the name slot's render logic
// needs the `settings` object closed over at build time, so a DOM re-query alone can't rebuild it
// the way that sibling does. Re-set on every `buildNameSlot` call (each `renderGameStart`), so it
// always points at the currently-mounted slot; stale only across a full screen swap (game start →
// in-game), where nothing calls it anyway (see `refreshNameSlot`'s own doc).
let nameSlotRenderState = null;

// Returns { slot, renderState } rather than the bare slot: the Multiplayer section's own
// register doorway (its unregistered-branch nudge CTA) needs `renderState` too, so a
// register there also updates this slot rather than leaving it stale — see
// buildGameStartContent's call site and game-start-multiplayer-section.js#buildMultiplayerSection.
function buildNameSlot(settings) {
  const slot = document.createElement("div");
  slot.className = "game-start-name-slot";
  const renderState = () => {
    slot.innerHTML = "";
    const registered = getRegisteredName();
    // A registered name is server-authoritative (slice 2c humanName precedence) — the
    // session send follows it so the client and server agree without waiting for the
    // server's override.
    if (registered != null) settings.playerName = registered;
    slot.appendChild(buildIdentityTrigger(settings, registered, renderState));
  };
  renderState();
  nameSlotRenderState = renderState;
  return { slot, renderState };
}

/**
 * Re-renders the start-screen name slot in place — the real refresh `game-menu.js`'s identity
 * entry passes into `openProfileOverlay` when the pause menu opens from the start screen
 * (`state.phase == null`), so a register/login/logout made through that menu entry keeps the slot
 * in sync exactly like the slot's own trigger already does (`buildIdentityTrigger` above). The
 * in-game entry keeps passing a plain no-op instead: no start screen is mounted then, and this
 * function would just re-render a detached slot from the last visit.
 */
export function refreshNameSlot() {
  nameSlotRenderState?.();
}

/**
 * The name slot's single clickable identity row (DB slice 2c identity-overlay rework) — a
 * doorway into the profile overlay (register/login/logout/change-password plus the settings
 * sections), never an editable
 * input itself. Registered shows the fixed server name led by the Knight persona bust (the
 * identity-action-confirmation design — the mark doubles as the doorway affordance the faint
 * underline under-delivered; the underline stays too) with no quill (a registered name can't be
 * renamed); anonymous shows the free-text name or the "Who's playing?" placeholder, both with the
 * quill as a decorative edit hint (the button itself, not the icon, carries the click handler and
 * aria-label).
 */
function buildIdentityTrigger(settings, registered, renderState) {
  const row = document.createElement("button");
  row.type = "button";
  row.className = "game-start-identity-trigger";
  row.setAttribute("aria-label", t("gameStartNameAria"));
  const label = document.createElement("span");
  label.className = "game-start-name-display";
  if (registered != null) {
    label.textContent = registered;
    row.appendChild(buildNamePersona());
    // A faint underline instead marks the name a doorway into account settings — an at-rest cue
    // the bare text otherwise lacked (the persona bust now doubles as the primary cue).
    row.classList.add("game-start-identity-trigger--account");
  } else if (settings.playerName) {
    label.textContent = settings.playerName;
  } else {
    label.textContent = t("gameStartNamePlaceholder");
    row.classList.add("game-start-identity-trigger--empty");
  }
  row.appendChild(label);
  if (registered == null) {
    const edit = document.createElement("span");
    edit.className = "game-start-name-edit";
    edit.setAttribute("aria-hidden", "true");
    edit.appendChild(createQuillIcon());
    row.appendChild(edit);
  }
  row.addEventListener("click", () =>
    openProfileOverlay(renderState, { screen: "start", source: "name-slot" }),
  );
  return row;
}

/** The registered row's Knight bust, `IDENTITY_COLOR.human` (white — needs no vellum rim on
 *  petrol). Decorative like the quill (aria-hidden; the row itself carries the aria-label) —
 *  and the etch host the identity-action-confirmation beat finds by selector on register/login. */
function buildNamePersona() {
  const persona = document.createElement("span");
  persona.className = "game-start-name-persona";
  persona.setAttribute("aria-hidden", "true");
  persona.appendChild(
    createPersonaKnight(NAME_PERSONA_SIZE, IDENTITY_COLOR.human),
  );
  return persona;
}

/** Heritage-style quill icon — closed feather silhouette with tapered nib. */
function createQuillIcon() {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  // why: 24 is the viewBox base; skat.css `.game-start-name-edit svg` mirrors it as the
  // base px of its elastic override — change one side, change the other.
  svg.setAttribute("width", "24");
  svg.setAttribute("height", "24");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "1.4");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  // Feather body curves from the top-right tip down to the nib at the bottom-left.
  const body = document.createElementNS("http://www.w3.org/2000/svg", "path");
  body.setAttribute("d", "M20 3 C 22 7 21 13 16 16 C 12 18 8 19 6 19 L 20 3 Z");
  body.setAttribute("fill", "currentColor");
  body.setAttribute("fill-opacity", "0.18");
  svg.appendChild(body);
  // Spine extends past the body to form the visible nib tail.
  const spine = document.createElementNS("http://www.w3.org/2000/svg", "path");
  spine.setAttribute("d", "M20 3 L 3 21");
  svg.appendChild(spine);
  // Two barb hatches across the feather body for plume texture.
  for (const d of ["M16 6 L 11 8", "M13 9 L 8 12"]) {
    const barb = document.createElementNS("http://www.w3.org/2000/svg", "path");
    barb.setAttribute("d", d);
    barb.setAttribute("stroke-width", "1");
    barb.setAttribute("stroke-opacity", "0.7");
    svg.appendChild(barb);
  }
  return svg;
}

function buildModeCards(settings) {
  const container = document.createElement("div");
  container.className = "mode-cards";
  const modes = [
    {
      key: "SKAT",
      label: t("modeSkat"),
      iconFn: () => createChalice(MODE_ICON_SIZE, MODE_ICON_COLOR),
    },
    {
      key: "RAMSCH",
      label: t("modeRamsch"),
      iconFn: () => createFallenChalice(MODE_ICON_SIZE, MODE_ICON_COLOR),
    },
  ];
  for (const mode of modes)
    container.appendChild(createModeCard(mode, settings, container));
  return container;
}

function createModeCard(mode, settings, container) {
  const card = document.createElement("div");
  card.className =
    "mode-card" + (mode.key === settings.gameMode ? " selected" : "");
  card.dataset.mode = mode.key;
  if (hasActiveRun(mode.key)) {
    card.classList.add("has-active-run");
  }
  card.appendChild(buildModeIcon(mode));
  card.appendChild(buildModeLabel(mode.label));
  card.appendChild(buildModeRunBadge());
  updateModeCardBadge(card);
  card.addEventListener("click", () => {
    container
      .querySelectorAll(".mode-card")
      .forEach((c) => c.classList.remove("selected"));
    card.classList.add("selected");
    settings.gameMode = mode.key;
    writeLastSelected(settings.gameMode);
    refreshActiveRunVisuals(settings);
  });
  return card;
}

function buildModeIcon(mode) {
  const iconDiv = document.createElement("div");
  iconDiv.className = "mode-icon";
  const iconSvg = mode.iconFn();
  makeRecolorable(iconSvg);
  iconDiv.appendChild(iconSvg);
  return iconDiv;
}

// The label is glyph-path SVG (not DOM text) so the panel-etch entrance can
// inscribe it onto the card — the card is a primary mark, so its label etches
// rather than fades. A language without extracted glyphs falls back to plain text,
// which then blooms with the card surface. Resolve --text-primary to a concrete
// color: the etch sets stroke attrs / tweens fill, which don't resolve CSS vars
// (same as the wordmark).
function buildModeLabel(word) {
  const labelDiv = document.createElement("div");
  labelDiv.className = "mode-label";
  const labelSvg = buildModeLabelSvg(word, resolveInkColor());
  if (labelSvg != null) labelDiv.appendChild(labelSvg);
  else labelDiv.textContent = word;
  return labelDiv;
}

// Always created, content-empty — updateModeCardBadge (game-start-active-runs.js) fills it in
// synchronously right after, with the placeholder or the run text. Creating it here unconditionally
// (rather than letting updateModeCardBadge add/remove it) keeps a mode card's rendered box identical
// with and without an active run, so the async /api/runs patch can't move anything below the cards.
function buildModeRunBadge() {
  const badge = document.createElement("div");
  badge.className = "mode-run-badge";
  return badge;
}

// why: the .btn-primary label color (#082f49 Dark Petrol on Gold) as a concrete
// value — the etch tweens SVG `fill`, which can't resolve `currentColor`/a token.
const PLAY_LABEL_INK = "#082f49";

function buildPlayButton(settings) {
  const btn = document.createElement("button");
  btn.className = "btn-primary play-button btn-plate";
  // Enter the gate to play — the label word inscribes first, the gate icon walks
  // in after it (the in-game Quit button is icon-only; this is the bigger
  // commitment). The label is glyph-path SVG (not DOM text) so the
  // entrance can etch it onto the button; a plain span falls back when the
  // active language has no extracted glyphs.
  const word = playLabelWord();
  const label =
    buildPlayLabelSvg(word, PLAY_LABEL_INK) ?? textLabelFallback(word);
  btn.appendChild(label);
  const icon = createGateEnter();
  icon.classList.add("play-button-icon");
  btn.appendChild(icon);
  btn.addEventListener("click", () => commitAndPlay(btn, settings));
  armButtonPlate(btn);
  return btn;
}

function textLabelFallback(word) {
  const span = document.createElement("span");
  span.className = "play-button-label";
  span.textContent = word;
  return span;
}

/**
 * Disassemble the start screen while the session request is already in
 * flight: the create POST fires at click time and its round trip rides under
 * the exit animation, but startSession holds the response until the exit
 * settles — so the exit is still fully visible before next-round's render
 * swaps the board in. A failed exit must not block the game (animation-error
 * convention), hence the catch on the exit promise. Double-click is guarded
 * by the button's disabled flag; making the whole screen inert at click time
 * stops a mid-flight click from mutating `settings` before startSession reads
 * it synchronously below.
 *
 * Races ensureGameUi() (lazy-game-graph-boot-seam) alongside the exit + session start — the
 * deal render itself is structurally gated elsewhere (the onAfterApi seam adapter in boot.js
 * awaits ensureGameUi() too), so this call is a warm-path optimization, not a correctness
 * dependency. The conditional cold-boot indicator owns its own lifetime via
 * showColdBootIndicatorIfNeeded below.
 */
async function commitAndPlay(btn, settings) {
  if (btn.disabled) return;
  enterFullscreen(); // must run synchronously off this gesture — see its header
  btn.disabled = true;
  const screen = document.querySelector(".game-start");
  screen.style.pointerEvents = "none";
  const exitDone = playGameStartExit(screen).catch((e) => {
    console.warn("[game-start-exit]", e.message);
  });
  const graphReady = ensureGameUi();
  showColdBootIndicatorIfNeeded(screen, exitDone, graphReady);
  startSession(settings, sessionHooks(), exitDone);
}

/**
 * Mount a loading treatment on the emptied game-start stage — but only if the exit has already
 * settled by the time the graph resolves, never on the warm path (Andre's fixed constraint: the
 * indicator must never show unconditionally). warmGameGraph()'s idle-scheduled preload keeps this
 * the rare case: bytes are usually already fetched, so ensureGameUi()'s import()+initGameGraph()
 * normally resolves well inside the exit's ~0.5s. Torn down the instant the graph resolves,
 * whether or not it was ever mounted.
 */
function showColdBootIndicatorIfNeeded(screen, exitDone, graphReady) {
  let settled = false;
  let indicator = null;
  const end = () => {
    settled = true;
    indicator?.remove();
  };
  graphReady.then(end, () => {
    // The graph failed to load. game-start-session routes this same rejection (via the onAfterApi
    // adapter, which awaits the same memoized promise) into the standard error recovery
    // (setHadApiError + fetchState → the start screen re-renders). This racer only owns the
    // indicator's lifetime, so all it does on failure is end it: tear the treatment down if it was
    // mounted, and never mount it if the exit hasn't settled yet. Attaching this handler also keeps
    // the failure from surfacing as an unhandled rejection off this branch of the shared promise.
    end();
  });
  exitDone.then(() => {
    if (settled) return; // warm path (or already-failed) — nothing to show, ever
    indicator = buildColdBootIndicator();
    screen.appendChild(indicator);
  });
}

/** Functional-minimum visual for the cold-Play wait (shape-live — open question 1, refine on
 *  sight): the boot overlay's inkwell language (app-loader.js's track/fill, `app-loader-fill`
 *  keyframes) at reduced scale — the emptied stage's only other loading treatment is that same
 *  overlay's dissolve, so this reads as its sibling rather than a new vocabulary. */
function buildColdBootIndicator() {
  const indicator = document.createElement("div");
  indicator.className = "commit-play-indicator";
  const track = document.createElement("div");
  track.className = "commit-play-indicator-track";
  const fill = document.createElement("span");
  fill.className = "commit-play-indicator-fill";
  track.appendChild(fill);
  indicator.appendChild(track);
  return indicator;
}

function sessionHooks() {
  return {
    hasActiveRun,
    runsReady: activeRunsReady,
  };
}
